import React from 'react';
import { StaticIcons } from '../StaticIcon/types';

export const GLOBAL_THEME_KEY = 'globalTheme';
export const GLOBAL_DEVICE_KEY = 'isMobile';

export enum ThemeEnum {
  default = 'default',
  pickit = 'pickit',
  pickers = 'pickers',
}

export type ThemeState = {
  isMobile: boolean;
  actualTheme: ThemeType;
  themeName: ThemeEnum;
  changeTheme: (theme: ThemeEnum) => void;
  changeDevice: (isMobile: boolean) => void;
};

export type ThemeProviderProps = {
  children: React.ReactNode;
  theme?: ThemeEnum;
  mobile?: boolean;
};

export type SizesType = 'small' | 'medium' | 'large';
export type TableSizesType = 'small' | 'medium' | 'large' | 'fullHeight';
export type TableFontSizesType = 'small' | 'large';
export type IconSizesType =
  | SizesType
  | 'extra-large'
  | 'extra-small'
  | 'tab-size-large'
  | 'tab-size-medium';
export type ComplexIconSizesType = SizesType | 'extra-small' | 'tab-size-large' | 'tab-size-medium';
export type InputSizesType = 'standard' | 'small';
export type InputVariantType = 'underline' | 'contained-label' | 'top-aligned-label';
export type StatusAlertType = 'error' | 'success' | 'warning' | 'info';
export type OverlayPositionType = 'auto' | 'bottom' | 'top';
export type StatusInformativeNotificationType = StatusAlertType | 'default';
export type StatusButtonType = StatusInformativeNotificationType | 'light' | 'dark';
export type StatusTagType = StatusAlertType | 'default' | 'disabled';
export type StatusType = StatusButtonType | StatusTagType;
export type StatusNavBarType = 'default' | 'dark';

type ColorType = {
  color: string;
  background?: string;
  contrast: string;
  hover: string;
  active: string;
  opacity: string;
};

type NotificationColorType = {
  border: string;
  background: string;
};

type ButtonSizeType = {
  fontSize: number;
  lineHeight: number;
  height: number;
  padding: number;
  spinner: number;
  circularPadding: number;
};

export type CheckboxSizeType = {
  box: number;
  fontSize: number;
  lineHeight: number;
};

export type RadioButtonSize = {
  outerCircle: number;
  innerCircle: number;
  border: number;
  lineHeight: number;
  fontSize: number;
};

export type BasicIconSize = {
  size: number;
  stroke: number;
};

export type ComplexIconSize = {
  size: number;
  stroke: number;
};

export type CollapsibleType = {
  duration: number;
};

export type InputSizeType = {
  fontSize: number;
  lineHeight: number;
  height: { [k in InputVariantType]: number };
  paddingBottom: number;
};

export type InputErrorType = {
  background: string;
  fontSize: number;
  lineHeight: number;
  containerHeight: number;
};

export type LayersType = {
  baseLayer: number;
  intermediateBaseLayer: number; // desplegables sobre pantalla base
  intermediateMediumLayer: number; // navbar
  mediumLayer: number; // modal
  intermediateTopLayer: number; // desplegables sobre modal
  topLayer: number; // alertas
};

export type TableSize = {
  rowHeight: { [key in TableFontSizesType]: number };
  maxHeight: { [key in TableFontSizesType]: string };
  maxLines: number;
};

export type ThemeType = {
  userLogoName: StaticIcons;
  grayA9: string;
  gray2D: string;
  gray4D: string;
  gray80: string;
  grayB8: string;
  grayCC: string;
  grayC4: string;
  grayC4Trans: string;
  grayF0: string;
  grayF5: string;
  black: string;
  white: string;
  blue: string;
  overlayBlack: string;
  layers: LayersType;
  status: { [k in StatusType]: ColorType };
  basicIcon: { [k in IconSizesType]: BasicIconSize };
  complexIcon: { [k in ComplexIconSizesType]: ComplexIconSize };
  button: {
    mobileFontSize: number;
    sizes: { [k in SizesType]: ButtonSizeType };
    defaultLinkColor: string;
  };
  checkbox: {
    colors: { background: string };
    sizes: { [k in SizesType]: CheckboxSizeType };
  };
  datePicker: {
    default: string;
    background: string;
  };
  sidebar: {
    background: string;
    color: string;
    node: { color: string; active: string };
    item: { color: string; active: string };
  };
  tooltip: {
    background: string;
    color: string;
    lineHeight: number;
  };
  contextMenu: { color: string; active: string };
  radiobutton: {
    sizes: { [k in SizesType]: RadioButtonSize };
  };
  collapsible: CollapsibleType;
  toast: {
    height: number;
    types: {
      default: {
        backgroundColor: string;
        iconColor: string;
      };
      confirmation: {
        backgroundColor: string;
        iconColor: string;
      };
      alert: {
        backgroundColor: string;
        iconColor: string;
      };
      info: {
        backgroundColor: string;
        iconColor: string;
      };
      error: {
        backgroundColor: string;
        iconColor: string;
      };
    };
  };
  input: {
    sizes: { [k in InputSizesType]: InputSizeType };
    error: InputErrorType;
    spaceBetweenInputs: number;
  };
  tabs: {
    active: string;
    disabled: string;
    height: { minimal: number; small: number; medium: number; large: number };
    width: number;
    minimalWidth: number;
    align: { left: string; center: string };
  };
  informationNotification: { [k in StatusInformativeNotificationType]: NotificationColorType };
  paginator: {
    active: string;
    hover: string;
  };
  toggleSwitch: {
    toggled: { [k in StatusInformativeNotificationType]: string };
    default: string;
    disabled: string;
    toggleSpan: string;
  };
  modal: {
    padding: number;
    animationDuration: number;
  };
  table: {
    sizes: { [k in TableSizesType]: TableSize };
    fontSizes: { [k in TableFontSizesType]: number };
    viewMoreButton: {
      default: string;
      disabled: string;
    };
  };
  statusStepper: {
    success: string;
    cancel: string;
    labelWidth: number;
    scrollHeightOffSet: number;
    scrollActionHeightOffSet: number;
    isExpandibleHeight: number;
    defaultHeight: number;
    lineHeight: number;
    buttonOffSet: number;
    singleLineHeight: number;
  };
  stepper: {
    success: {
      color: string;
      pipeColor: string;
      opacity: string;
    };
    error: {
      color: string;
      pipeColor: string;
      opacity: string;
    };
    actual: {
      color: string;
      pipeColor: string;
      opacity: string;
    };
    next: {
      color: string;
      pipeColor: string;
      opacity: string;
    };
  };
  loadBar: {
    progress: string;
    uncompleted: string;
  };
};
